/* global isMfaRequired */

import React, { useState, useEffect, useRef } from 'react';

import { Modal } from 'flowbite';

import MfaAddDevice from './MfaAddDevice';

import { formatDateWithTz } from 'Helpers/dateUtils';
import { modalOptions } from 'Helpers/modalOptions';

import Button from 'CommonComponents/Button';
import TextButton from 'CommonComponents/TextButton';

import PlusCircleIcon from 'Assets/icons/plus-circle.svg';
import TrashIcon from 'Assets/icons/trash.svg';

export default function MfaSetup(props) {
    const [isMfaActivated, setIsMfaActivated] = useState(props.isMfaActivated);
    const [devices, setDevices] = useState([]);
    const [qrCode, setQrCode] = useState(null);
    const [codeUrl, setCodeUrl] = useState('');
    const [hasAuthDevice, setHasAuthDevice] = useState(false);
    const [deviceRegisteredAt, setDeviceRegisteredAt] = useState(null);
    const [showModal, setShowModal] = useState(false);
    const [deviceAdded, setDeviceAdded] = useState(false);
    const [request, setRequest] = useState(false);

    const [mfaAddDeviceModalElement, setMfaAddDeviceModalElement] = useState(null);

    const mfaAddDeviceModalRef = useRef();

    useEffect(() => {
        fetchDeviceData();

        const inProgress = localStorage.getItem('replaceApp');
        if (inProgress) {
            replaceApp();
        }

        const deleteInProgress = localStorage.getItem('deleteApp');
        if (deleteInProgress) {
            deleteApp();
        }
        mfaAddDeviceModalRef && setMfaAddDeviceModalElement(new Modal(mfaAddDeviceModalRef.current, modalOptions(false)));
    }, []);

    useEffect(() => {
        if (isMfaActivated && showModal) {
            if (!props.isMfaConfirmed) {
                getQrCode();
            }
            fetchDeviceData();
        }
    }, [isMfaActivated]);

    useEffect(() => {
        if (deviceAdded && props.redirectAfterAdd) {
            window.location.href = props.redirectUrl;
        }
    }, [deviceAdded]);

    useEffect(() => {
        if (showModal) {
            mfaAddDeviceModalElement && mfaAddDeviceModalElement.show();
        }
    }, [showModal]);

    const activateMfa = () => {
        setRequest(true);
        axios
            .post(props.activateUrl, {})
            .then((res) => {
                setRequest(false);
                fetchDeviceData();
            })
            .catch((e) => {
                setRequest(false);
                if (e.response.status === 423) {
                    confirmPassword();
                }
            });
    };

    const getQrCode = () => {
        return axios
            .get(props.qrCodeUrl)
            .then((res) => {
                setQrCode(res.data.svg);
                setCodeUrl(res.data.url);
            })
            .catch((e) => {
                if (e.response.status === 423) {
                    confirmPassword();
                }
                console.log(e);
            });
    };

    const fetchDeviceData = () => {
        axios
            .get(props.mfaSettingsUrl)
            .then((res) => {
                setHasAuthDevice(res.data.hasAuthDevice);
                setDeviceRegisteredAt(res.data.deviceRegisteredAt);
                setIsMfaActivated(res.data.isMfaActivated);
                setDevices(res.data.devices);
            })
            .catch((e) => {
                console.log(e);
            });
    };

    const showAddDeviceModal = () => {
        getQrCode().then((res) => {
            setShowModal(true);
        });
    };
    const closeAddDeviceModal = () => {
        fetchDeviceData();
        mfaAddDeviceModalElement && mfaAddDeviceModalElement.hide();
        setTimeout(() => {
            setShowModal(false);
        }, 200);
    };

    const confirmPassword = () => {
        window.location.href = '/user/confirm-password';
    };

    const deleteApp = () => {
        const inProgress = localStorage.getItem('deleteApp');
        const deleteConfirmed = localStorage.getItem('deleteConfirmed');

        const confirmMessage = $t('Are you sure you want to disable the current two-factor device?');

        if (!inProgress) {
            localStorage.setItem('deleteApp', `1`);
        }

        if (!deleteConfirmed) {
            if (confirm(confirmMessage)) {
                removeApp();
            }
        } else {
            removeApp();
        }
    };

    const replaceApp = () => {
        const inProgress = localStorage.getItem('replaceApp');
        const replaceConfirmed = localStorage.getItem('replaceAppConfirmed');

        const confirmMessage = $t('Are you sure you want to replace the current two-factor device?');

        if (!inProgress) {
            localStorage.setItem('replaceApp', `1`);
        }

        if (!replaceConfirmed) {
            if (confirm(confirmMessage)) {
                replace();
            }
        } else {
            replace();
        }
    };

    const replace = () => {
        const isAppDeleted = localStorage.getItem('replaceAppDeleted');
        localStorage.setItem('replaceAppConfirmed', '1');

        if (isAppDeleted) {
            activateAfterDelete();
        } else {
            removeApp();
        }
    };

    const removeApp = () => {
        axios
            .delete(props.deactivateUrl)
            .then((res) => {
                localStorage.setItem('replaceAppDeleted', '1');
                localStorage.setItem('appDeleted', '1');
                activateAfterDelete();
            })
            .catch((e) => {
                if (e.response.status === 423) {
                    confirmPassword();
                }
            });
    };

    const activateAfterDelete = () => {
        setRequest(true);
        axios
            .post(props.activateUrl, {})
            .then((response) => {
                setRequest(false);
                getQrCode();
                fetchDeviceData();

                localStorage.removeItem('replaceApp');
                localStorage.removeItem('replaceAppConfirmed');
                localStorage.removeItem('replaceAppDeleted');

                localStorage.removeItem('appDeleted');
                localStorage.removeItem('deleteApp');
                localStorage.removeItem('deleteConfirmed');
            })
            .catch((er) => {
                setRequest(false);
                if (er.response.status === 423) {
                    confirmPassword();
                }
            });
    };

    return (
        <div className="row mt-3">
            <div className="col-12">
                <div className="row">
                    {!isMfaActivated && (
                        <div className="col-12 mb-3">
                            {!!isMfaRequired && (
                                <div className="alert alert-warning" role="alert">
                                    {$t('At least one of your InBudget Organisations requires MFA. Please set this up now.')}
                                </div>
                            )}
                            <Button variant="primary" onClick={activateMfa} disabled={request}>
                                {$t('Enable MFA')}
                            </Button>
                        </div>
                    )}
                    {!!isMfaActivated && (
                        <div className="col-12 mb-3">
                            <div>
                                <div className="row mt-3">
                                    <div className="col-12">
                                        <div className="card bg-white border-0 rounded-15px py-2 shadow">
                                            <div className="card-body">
                                                <div className="row">
                                                    <div className="col-12 mb-2">
                                                        <h4>Authenticator App</h4>
                                                    </div>
                                                </div>
                                                <div className="row d-flex justify-content-end">
                                                    <div className="m-3">
                                                        {!hasAuthDevice && !deviceRegisteredAt && (
                                                            <Button variant="primary" className="tw-mr-2" onClick={showAddDeviceModal}>
                                                                <PlusCircleIcon className="tw-w-3.5 tw-h-3.5 tw-mr-2" />
                                                                <span>{$t('Add')}</span>
                                                            </Button>
                                                        )}
                                                    </div>
                                                </div>
                                                <div className="row d-block mx-2">
                                                    <div id={'device-table'} className={'inbud-table_wrapper'}>
                                                        <table className={'inbud-table'}>
                                                            <thead>
                                                                <tr className={'text-blue bg-white'}>
                                                                    <th style={{ width: '25%' }}>{$t('Device')}</th>
                                                                    <th style={{ width: '25%' }}>{$t('Date Registered')}</th>
                                                                    <th style={{ width: '25%' }}>{$t('Last Used')}</th>
                                                                    <th style={{ width: '25%' }}>{$t('Actions')}</th>
                                                                </tr>
                                                            </thead>
                                                            <tbody>
                                                                {devices.map((device) => {
                                                                    return (
                                                                        <tr key={device.id}>
                                                                            <td>{device.name || '-'}</td>
                                                                            <td className="tw-text-right">
                                                                                {device.created_at
                                                                                    ? formatDateWithTz(device.created_at, 'YYYY-MM-DD HH:mm:ss', 'DD/MM/YYYY')
                                                                                    : '-'}
                                                                            </td>
                                                                            <td className="tw-text-right">
                                                                                {device.latest_usage.used_at
                                                                                    ? formatDateWithTz(
                                                                                          device.latest_usage.used_at,
                                                                                          'YYYY-MM-DD HH:mm:ss',
                                                                                          'DD/MM/YYYY HH:mm:ss',
                                                                                      )
                                                                                    : '-'}
                                                                            </td>
                                                                            <td>
                                                                                {!device.deleted_at && !!props.isMfaRequired && (
                                                                                    <TextButton variant="danger" onClick={() => replaceApp()}>
                                                                                        {$t('Replace')}
                                                                                    </TextButton>
                                                                                )}
                                                                                {!device.deleted_at && !props.isMfaRequired && (
                                                                                    <TextButton variant="danger" onClick={() => deleteApp()}>
                                                                                        <TrashIcon className="tw-w-3 tw-h-3" />
                                                                                        {$t('Delete')}
                                                                                    </TextButton>
                                                                                )}
                                                                                {!!device.deleted_at && <span>{$t('Deleted')}</span>}
                                                                            </td>
                                                                        </tr>
                                                                    );
                                                                })}
                                                            </tbody>
                                                        </table>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                    <MfaAddDevice
                        modalId={'add-auth-device'}
                        modalRef={mfaAddDeviceModalRef}
                        modalElement={mfaAddDeviceModalElement}
                        closeHandler={closeAddDeviceModal}
                        showModal={showModal}
                        qrCode={qrCode}
                        codeUrl={codeUrl}
                        setDeviceAdded={setDeviceAdded}
                        confirmUrl={props.confirmUrl}
                        recoveryCodesUrl={props.recoveryCodesUrl}
                        sendDeviceAddedUrl={props.sendDeviceAddedUrl}
                    />
                </div>
            </div>
        </div>
    );
}
